import React, { useState } from "react";
import { FileText, Image, File, Download, X, Eye, FileSpreadsheet, Presentation } from "lucide-react";
import FileViewerModal from "./FileViewerModal";

const FILE_ICONS = {
  "application/pdf": FileText,
  "text/plain": FileText,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  "image/webp": Image,
};

// By name: a stored spreadsheet's MIME is whatever the uploader's browser said.
const EXTENSION_ICONS = {
  csv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  ppt: Presentation,
  pptx: Presentation,
};

const extensionOf = (name = "") => {
  const dot = String(name).lastIndexOf(".");
  return dot > 0 ? String(name).slice(dot + 1).toLowerCase() : "";
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const isImage = (type) => type?.startsWith("image/");

/* Converted to Tailwind utilities in Phase 5 (2026-08-24); FilePreview.scss deleted
 * (TOKEN_CONTRACT §6.1).
 *
 * `--compact` used to reach DOWN into `.file-preview__item` to change its padding.
 * With utilities the modifier is gone and the item picks its own padding from the
 * prop it already receives — one fewer selector, and the coupling is visible at the
 * element it affects rather than two rules away.
 *
 * `--destructive` -> `--danger`: the same colour under two names, and the
 * `0 84% 45%` fallback beside it was a different red that could never fire. */
/* The hover COLOURS are deliberately not in the shared base. Remove is the odd one
 * out, and putting `hover:tw-text-primary` in the base and `hover:tw-text-danger`
 * beside it would leave two equal-specificity rules on one element, decided by
 * Tailwind's emitted source order rather than by the order they are written in —
 * audit F16, in miniature. In the SCSS this worked only because `&--remove:hover`
 * happened to sit later in the file. Composing the base with exactly one hover pair
 * removes the question. */
const ACTION_BTN =
  "tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-1 tw-rounded-[4px] " +
  "tw-text-content-muted tw-flex tw-items-center tw-no-underline " +
  "tw-transition-[background-color,color]";
const ACTION_HOVER = "hover:tw-bg-surface-hover hover:tw-text-primary";
const ACTION_HOVER_REMOVE = "hover:tw-bg-danger/10 hover:tw-text-danger";

/**
 * FilePreview — Display list of uploaded file attachments.
 * Clicking a file opens it in the in-app FileViewerModal.
 *
 * Props:
 *   attachments   — [{ fileName, fileUrl, fileType, fileSize, fileKey, downloadUrl? }]
 *                   `downloadUrl`, when the server sends one, saves the file under
 *                   its own name; `fileUrl` is what the in-app viewer opens.
 *   onRemove      — (fileKey) => void  (if provided, shows remove button)
 *   compact       — compact layout (default: false)
 *   className     — extra class
 */
const FilePreview = ({ attachments = [], onRemove, compact = false, className = "" }) => {
  const [viewerFile, setViewerFile] = useState(null);

  if (!attachments.length) return null;

  const getIcon = (att) => {
    const Icon = EXTENSION_ICONS[extensionOf(att.fileName)] || FILE_ICONS[att.fileType] || File;
    return <Icon size={compact ? 16 : 18} />;
  };

  const openViewer = (att) => {
    if (att.fileUrl) setViewerFile(att);
  };

  return (
    <>
      <div
        className={`tw-flex tw-flex-col ${compact ? "tw-gap-1" : "tw-gap-2"} ${className}`}
      >
        {attachments.map((att, idx) => (
          <div
            key={att.fileKey || idx}
            className={`tw-flex tw-items-center tw-gap-2.5 tw-bg-surface-raised tw-border tw-border-line-soft tw-rounded-sm tw-transition-[border-color] hover:tw-border-line ${
              compact ? "tw-px-2.5 tw-py-1.5" : "tw-px-3 tw-py-2"
            }`}
          >
            {isImage(att.fileType) && att.fileUrl ? (
              <button
                type="button"
                className="tw-shrink-0 tw-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                onClick={() => openViewer(att)}
              >
                <img
                  src={att.fileUrl}
                  alt={att.fileName}
                  className="tw-w-10 tw-h-10 tw-object-cover tw-rounded-[4px] tw-border tw-border-line-soft"
                  loading="lazy"
                />
              </button>
            ) : (
              <span className="tw-shrink-0 tw-text-content-muted tw-flex tw-items-center">{getIcon(att)}</span>
            )}

            <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-px">
              <button
                type="button"
                className="tw-text-[0.8125rem] tw-font-medium tw-text-content tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-left hover:tw-text-primary hover:tw-underline"
                onClick={() => openViewer(att)}
                title={att.fileName}
              >
                {att.fileName}
              </button>
              {att.fileSize > 0 && (
                <span className="tw-text-[0.6875rem] tw-text-content-subtle">{formatFileSize(att.fileSize)}</span>
              )}
            </div>

            <div className="tw-shrink-0 tw-flex tw-items-center tw-gap-1">
              {att.fileUrl && (
                <button
                  type="button"
                  className={`${ACTION_BTN} ${ACTION_HOVER}`}
                  onClick={() => openViewer(att)}
                  title="View"
                  aria-label={`View ${att.fileName}`}
                >
                  <Eye size={14} />
                </button>
              )}
              {(att.downloadUrl || att.fileUrl) && (
                <a
                  href={att.downloadUrl || att.fileUrl}
                  download={att.fileName}
                  className={`${ACTION_BTN} ${ACTION_HOVER}`}
                  title="Download"
                  aria-label={`Download ${att.fileName}`}
                >
                  <Download size={14} />
                </a>
              )}
              {onRemove && (
                <button
                  type="button"
                  className={`${ACTION_BTN} ${ACTION_HOVER_REMOVE}`}
                  onClick={() => onRemove(att.fileKey)}
                  title="Remove"
                  aria-label={`Remove ${att.fileName}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <FileViewerModal
        show={!!viewerFile}
        onHide={() => setViewerFile(null)}
        fileUrl={viewerFile?.fileUrl}
        fileName={viewerFile?.fileName}
        fileType={viewerFile?.fileType}
        downloadUrl={viewerFile?.downloadUrl}
      />
    </>
  );
};

export default FilePreview;
